import type { ChatBrotherhood, ChatSource } from '@cruz/shared';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChatError, streamChat, type ChatErrorKind } from '../lib/api.ts';
import { load, save } from '../lib/storage.ts';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** Assistant only */
  status?: 'streaming' | 'done' | 'error';
  /** Tool being consulted right now */
  tool?: string;
  sources?: ChatSource[];
  /** Tools used for this answer, to avoid suggesting what it already covered */
  tools?: string[];
  /** The one brotherhood the answer was about: follow-ups are suggested on it */
  brotherhood?: ChatBrotherhood;
  /** The server's guard rewrote or withdrew the first answer */
  revised?: boolean;
  error?: ChatErrorKind | 'answer';
}

const STORAGE_KEY = 'cruz.conversation';
/** The API accepts 20 messages; older context is dropped first */
const MAX_HISTORY = 20;
const MAX_KEPT = 60;

let counter = 0;
const newId = () => `${Date.now().toString(36)}-${(counter++).toString(36)}`;

export function useChat() {
  const [messages, setMessages] = useState<Message[]>(() =>
    // An answer cut by closing the page is not resumed
    load<Message[]>(STORAGE_KEY, []).filter((m) => m.status !== 'streaming'),
  );
  const abortRef = useRef<AbortController | null>(null);
  // Latest messages for the callbacks (state updaters run later, not on the spot)
  const messagesRef = useRef(messages);
  useLayoutEffect(() => {
    messagesRef.current = messages;
  });
  const busy = messages.at(-1)?.status === 'streaming';

  useEffect(() => {
    if (!busy) save(STORAGE_KEY, messages.slice(-MAX_KEPT));
  }, [messages, busy]);

  const update = (id: string, change: (m: Message) => Partial<Message>) =>
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, ...change(m) } : m)));

  const send = useCallback(async (question: string) => {
    const text = question.trim();
    if (!text) return;
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;

    const user: Message = { id: newId(), role: 'user', text };
    const answer: Message = { id: newId(), role: 'assistant', text: '', status: 'streaming' };
    // Only completed exchanges are context for the model
    const history = messagesRef.current
      .filter((m) => m.text && (m.role === 'user' || m.status === 'done'))
      .map((m) => ({ role: m.role, content: m.text }));
    setMessages((ms) => [...ms, user, answer]);
    const conversation = [...history, { role: 'user' as const, content: text }].slice(-MAX_HISTORY);
    // The API wants the conversation to start with the user
    while (conversation[0]?.role === 'assistant') conversation.shift();

    try {
      for await (const event of streamChat(conversation, abort.signal)) {
        switch (event.type) {
          case 'tool':
            update(answer.id, (m) => ({
              tool: event.name,
              tools: [...(m.tools ?? []), event.name],
            }));
            break;
          case 'delta':
            update(answer.id, (m) => ({ text: m.text + event.text, tool: undefined }));
            break;
          case 'replace':
            update(answer.id, () => ({ text: event.text, revised: true, tool: undefined }));
            break;
          case 'done':
            update(answer.id, () => ({
              status: 'done',
              sources: event.sources,
              brotherhood: event.brotherhood,
              tool: undefined,
            }));
            break;
          case 'error':
            update(answer.id, () => ({
              status: 'error',
              error: 'answer',
              text: event.message,
              tool: undefined,
            }));
            break;
        }
      }
      // Stream ended without "done": the connection was cut
      update(answer.id, (m) =>
        m.status === 'streaming' ? { status: 'error', error: 'network', tool: undefined } : {},
      );
    } catch (err) {
      if (abort.signal.aborted) {
        update(answer.id, (m) => ({
          status: m.text ? 'done' : 'error',
          error: m.text ? undefined : 'network',
          tool: undefined,
        }));
        return;
      }
      const kind = err instanceof ChatError ? err.kind : 'server';
      update(answer.id, () => ({ status: 'error', error: kind, tool: undefined }));
    }
  }, []);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  /** Asks the last failed question again, in place of the failed exchange. */
  const retry = useCallback(() => {
    const ms = messagesRef.current;
    const last = ms.at(-1);
    const prev = ms.at(-2);
    if (last?.status !== 'error' || prev?.role !== 'user') return;
    messagesRef.current = ms.slice(0, -2);
    setMessages(messagesRef.current);
    void send(prev.text);
  }, [send]);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
  }, []);

  return { messages, busy, send, stop, retry, clear };
}
